import type { AssetInstance } from '@/types/assetTemplate';
import {
  compareInstanceNumbers,
  isInInstanceRange,
} from './instanceNumber';
import type { UnitLabelItem } from './unitLabelPdfGenerator';

export type { UnitLabelItem };

const PAGE_LIMIT = 200;
const MAX_INSTANCES = 5000;

export async function fetchAllInstancesForTemplate(templateId: string): Promise<AssetInstance[]> {
  const token = localStorage.getItem('token');
  const all: AssetInstance[] = [];
  let page = 1;

  while (all.length < MAX_INSTANCES) {
    const res = await fetch(
      `/api/asset-instances?template_id=${encodeURIComponent(templateId)}&page=${page}&limit=${PAGE_LIMIT}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) throw new Error('Failed to load units for labels');
    const json = await res.json();
    const batch: AssetInstance[] = json.success ? json.data : json;
    all.push(...batch);
    const pages = json.pagination?.totalPages ?? json.pagination?.pages ?? 1;
    if (page >= pages || batch.length === 0) break;
    page += 1;
  }

  if (all.length >= MAX_INSTANCES) {
    throw new Error(`Too many units (>${MAX_INSTANCES}). Contact support for bulk label export.`);
  }
  return all.sort((a, b) => compareInstanceNumbers(a.instance_number, b.instance_number));
}

export function filterInstancesByRange(
  instances: AssetInstance[],
  from: string,
  to: string,
): AssetInstance[] {
  const f = from.trim();
  const t = to.trim();
  if (!f || !t) throw new Error('From and To unit codes are required');
  if (compareInstanceNumbers(f, t) > 0) throw new Error('From unit must come before To unit');

  const matched = instances.filter((i) => isInInstanceRange(i.instance_number, f, t));
  if (matched.length === 0) throw new Error('No units found in that range');
  return matched;
}

export function toUnitLabelItems(
  instances: AssetInstance[],
  productName: string,
  templateCode?: string,
): UnitLabelItem[] {
  return instances.map((i) => ({
    productName,
    templateCode: templateCode ?? i.template?.template_code ?? i.instance_number.replace(/-\d+$/, ''),
    instanceNumber: i.instance_number,
  }));
}
